import { useEffect, useRef, useState } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import librarySaveCopy from '@/i18n/ns/library-save';
import { Button } from '@/components/ui/button';
import { SessionChangedError } from '@/lib/session-guard';
import { CanvasHeaderTools } from '../canvas-footer';
import { savedArtifactCanvasTarget } from '../artifacts/routes';

/** The Library item a save produced or an Artifact already is: the genuine saved tuple (the existing Library entry). */
export type SavedItem = {
  artifactId: string;
  versionId: string;
  revisionId: string;
  /** Known for an Artifact already in the Library; an App's is read when it is opened. */
  versionNumber?: number;
  /** The Library item's own purpose, kept as given; the editor view when absent. */
  purpose?: 'viewer' | 'editor';
};
export type SaveToLibraryWriter = {
  /**
   * Idempotent for the ITEM: saving again updates the same Library item, an unchanged retry writes nothing new. The
   * owner of the writer (App: the thread's App; Artifact: Chat's committed output) is the only place that writes.
   */
  save(): Promise<SaveResult>;
  /** Re-reads whether an unconfirmed save landed (default: save again, which is idempotent for the item). */
  check?(): Promise<SaveResult>;
};
/** `unconfirmed`: the write may or may not have landed. It is never shown as a plain failure; Check again settles it. */
export type SaveResult =
  | { saved: SavedItem; outputAvailable?: boolean; status?: undefined }
  | { status: 'unconfirmed'; saved?: undefined; outputAvailable?: undefined };

/** The existing saved canvas of an item (what Open in the Library shows); null until its number is known. */
export const savedItemTarget = (saved: SavedItem) =>
  saved.versionNumber === undefined
    ? null
    : savedArtifactCanvasTarget({
        artifactId: saved.artifactId,
        revisionId: saved.revisionId,
        versionId: saved.versionId,
        versionNumber: saved.versionNumber,
        purpose: saved.purpose ?? 'editor',
      });

type Phase = 'idle' | 'saving' | 'saved' | 'unconfirmed' | 'failed';

/**
 * The ONE Save to Library control of a canvas, drawn in the canvas header. Already-saved items (an Artifact Chat
 * committed) start as Saved with no write. Saving is one idempotent writer call per click; a failure is said and the
 * same button tries again. A different item (`itemKey`) starts fresh; a replaced session shows nothing more.
 */
export function SaveToLibraryControl({
  itemKey,
  saved,
  writer,
  onOpen,
}: {
  itemKey: string;
  title?: string;
  saved?: SavedItem | null;
  /** Absent for an item that is only ever Saved/Open (an Artifact Chat committed): there is nothing to write. */
  writer?: SaveToLibraryWriter;
  /** Opens the item's existing Library entry. May fail (a read): the item stays Saved and only Open is retried. */
  onOpen: (saved: SavedItem) => Promise<void> | void;
}) {
  const copy = useNamespaceCopy(librarySaveCopy);
  const [state, setState] = useState<{ key: string; phase: Phase; saved: SavedItem | null }>({
    key: itemKey,
    phase: saved ? 'saved' : 'idle',
    saved: saved ?? null,
  });
  const working = useRef(false);
  const [openFailed, setOpenFailed] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  // Another item on the same canvas slot (a later snapshot, another Artifact) starts from its own facts.
  const fresh = {
    key: itemKey,
    phase: (saved ? 'saved' : 'idle') as Phase,
    saved: saved ?? null,
  };
  const own = state.key === itemKey ? state : fresh;
  // A genuine Library item arriving (a late Chat commit) is authoritative: Saved, with no writer call.
  const current =
    saved && own.phase !== 'saving' ? { ...own, phase: 'saved' as Phase, saved } : own;
  const save = async (again = false) => {
    if (working.current || !writer) return;
    working.current = true;
    const key = itemKey;
    setState({ key, phase: 'saving', saved: current.saved });
    try {
      const result = await (again && writer.check ? writer.check() : writer.save());
      if (!alive.current) return;
      if (result.status === 'unconfirmed') {
        setState({ key, phase: 'unconfirmed', saved: null });
        return;
      }
      setState({
        key,
        phase: 'saved',
        saved: result.saved,
      });
    } catch (error) {
      if (alive.current && !(error instanceof SessionChangedError))
        setState({ key, phase: 'failed', saved: current.saved });
    } finally {
      working.current = false;
    }
  };
  const open = async () => {
    if (!current.saved) return;
    setOpenFailed(false);
    try {
      await onOpen(current.saved);
    } catch (error) {
      if (alive.current && !(error instanceof SessionChangedError)) setOpenFailed(true);
    }
  };
  return (
    <CanvasHeaderTools>
      <span data-testid="save-to-library" data-phase={current.phase}>
        {current.phase === 'saved' && current.saved ? (
          <>
            <span data-testid="save-to-library-saved">{copy.saved}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="save-to-library-open"
              onClick={() => void open()}
            >
              {copy.open}
            </Button>
          </>
        ) : current.phase === 'unconfirmed' ? (
          <>
            <span role="status">{copy.unconfirmed}</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="save-to-library-check"
              onClick={() => void save(true)}
            >
              {copy.checkAgain}
            </Button>
          </>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="save-to-library-save"
            disabled={current.phase === 'saving'}
            onClick={() => void save()}
          >
            {current.phase === 'saving' ? copy.saving : copy.save}
          </Button>
        )}
        {current.phase === 'failed' && <span role="alert">{copy.failed}</span>}
        {current.phase === 'saved' && openFailed && <span role="alert">{copy.openFailed}</span>}
      </span>
    </CanvasHeaderTools>
  );
}
